// Inspired by https://github.com/ianstormtaylor/slate/issues/4162#issuecomment-812618312

import { Editor, Point, Range, Path, Text, Node, Element, BaseRange, NodeEntry } from 'slate';
import { CustomEditor, CustomText } from '../../../../models/slate-editor';

export const currentWord = (
  editor: CustomEditor,
  at: Range,
  options: {
    terminator?: string[];
    include?: boolean;
    directions?: 'both' | 'left' | 'right';
  } = {}
): Range | undefined => {
  const { terminator = [' '], include = false, directions = 'both' } = options;

  const { selection } = editor;
  if (!selection) return;

  // Get start and end, modify it as we move along.
  let [start, end] = Range.edges(at);

  let point: Point = start;

  const move = (direction: 'right' | 'left'): boolean => {
    const next =
      direction === 'right'
        ? Editor.after(editor, point, {
            unit: 'character'
          })
        : Editor.before(editor, point, { unit: 'character' });

    const wordNext =
      next &&
      Editor.string(editor, direction === 'right' ? { anchor: point, focus: next } : { anchor: next, focus: point });

    const last = wordNext && wordNext[direction === 'right' ? 0 : wordNext.length - 1];
    if (next && last && !terminator.includes(last)) {
      point = next;

      if (point.offset === 0) {
        // Means we've wrapped to beginning of another block
        return false;
      }
    } else {
      return false;
    }

    return true;
  };

  // Move point and update start & end ranges

  // Move forwards
  if (directions !== 'left') {
    point = end;
    while (move('right')) {
      // do nothing
    }
    end = point;
  }

  // Move backwards
  if (directions !== 'right') {
    point = start;
    while (move('left')) {
      // do nothing
    }
    start = point;
  }

  if (include) {
    return {
      anchor: Editor.before(editor, start, { unit: 'offset' }) ?? start,
      focus: Editor.after(editor, end, { unit: 'offset' }) ?? end
    };
  }

  return { anchor: start, focus: end };
};

const getParent = (editor: CustomEditor, path: Path): Node | null => {
  // Ensure the path is not at the root (because the root node doesn't have a parent)
  if (path.length === 0) {
    return null;
  }

  const parentPath = Path.parent(path);
  return Node.get(editor, parentPath);
};

export const getBlockWords = (editor: CustomEditor): BaseRange[] => {
  const ranges: BaseRange[] = [];
  const regex = /([^\s\n]+)/g;
  const nodes: NodeEntry<CustomText>[] = Array.from(Editor.nodes(editor, { at: [], match: n => Text.isText(n) }));
  for (const [node, path] of nodes) {
    const parent = getParent(editor, path);
    if (Element.isElement(parent) && parent.type === 'paragraph') {
      let match;

      while ((match = regex.exec(node.text)) !== null) {
        const [matchedText, index] = [match[0], match.index];

        const range = {
          anchor: { path, offset: index },
          focus: { path, offset: index + matchedText.length }
        };

        ranges.push(range);
      }
    }
    // we can only get paragraphs since mentions and links have text value of '', and they won't be fetched here.
  }
  return ranges;
};
